/**
 * SharePoint Online file picker — lets users browse sites, document libraries,
 * and optionally folders to select an IND document library.
 *
 * A2F-423: browsing goes through Chrona's backend, which uses the org's
 * app-only Graph credentials — NOT the browser's delegated Entra token. This
 * removes two failure modes of the old delegated picker: Microsoft's flaky
 * `/sites?search=` endpoint (intermittent `500 generalException`) and the
 * ephemeral delegated token that was lost on every page reload. The list now
 * also shows exactly the sites the app principal can read, so a selection can't
 * pass here and then 403 at ingest.
 */

import { useState, useEffect } from 'react';
import {
  Link2,
  Globe,
  HardDrive,
  Folder,
  ChevronRight,
  ArrowLeft,
  LoaderCircle,
  Check,
} from 'lucide-react';
import { api, type SPOSiteInfo, type SPODriveInfo, type SPOFolderInfo } from '@/lib/api';
import { type SPOSelection } from '@/lib/graphApi';

type PickerStep = 'sites' | 'drives' | 'folders';

interface SPOFilePickerProps {
  onSelect: (selection: SPOSelection) => void;
}

/** Human-readable message from an apiFetch failure (ApiError or Error). */
function errMessage(err: unknown, fallback: string): string {
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}

export function SPOFilePicker({ onSelect }: SPOFilePickerProps) {
  // Navigation state
  const [step, setStep] = useState<PickerStep>('sites');
  const [selectedSite, setSelectedSite] = useState<SPOSiteInfo | null>(null);
  const [selectedDrive, setSelectedDrive] = useState<SPODriveInfo | null>(null);
  const [folderStack, setFolderStack] = useState<SPOFolderInfo[]>([]);

  // Data state
  const [sites, setSites] = useState<SPOSiteInfo[]>([]);
  const [drives, setDrives] = useState<SPODriveInfo[]>([]);
  const [folders, setFolders] = useState<SPOFolderInfo[]>([]);

  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [urlInput, setUrlInput] = useState('');
  const [resolving, setResolving] = useState(false);

  // Load the org's configured site on mount. No search, no delegated token —
  // the backend resolves storage_config.site_id via the org's app-only creds.
  useEffect(() => {
    if (step !== 'sites' || sites.length > 0) return;
    let cancelled = false;

    (async () => {
      setLoading(true);
      setError('');
      try {
        const site = await api.projects.spo.site();
        if (!cancelled) setSites([site]);
      } catch (err) {
        if (!cancelled) {
          setError(
            errMessage(
              err,
              "Couldn't load your SharePoint site. Paste a site URL below to browse it directly.",
            ),
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  /** Resolve a pasted SharePoint URL to a site (bypasses site search entirely). */
  const handleResolveUrl = async () => {
    const value = urlInput.trim();
    if (!value) return;
    setResolving(true);
    setError('');
    try {
      const site = await api.projects.spo.site(value);
      setSites((prev) => (prev.some((s) => s.id === site.id) ? prev : [site, ...prev]));
      setUrlInput('');
    } catch (err) {
      setError(errMessage(err, "Couldn't resolve that SharePoint URL."));
    } finally {
      setResolving(false);
    }
  };

  const handleSelectSite = async (site: SPOSiteInfo) => {
    setSelectedSite(site);
    setStep('drives');
    setLoading(true);
    setError('');
    try {
      const { drives: result } = await api.projects.spo.drives(site.id);
      setDrives(result);
    } catch (err) {
      setError(errMessage(err, 'Failed to load document libraries.'));
    } finally {
      setLoading(false);
    }
  };

  const handleSelectDrive = async (drive: SPODriveInfo) => {
    setSelectedDrive(drive);
    setStep('folders');
    setFolderStack([]);
    setLoading(true);
    setError('');
    try {
      const { folders: result } = await api.projects.spo.folders(drive.id);
      setFolders(result);
    } catch (err) {
      setError(errMessage(err, 'Failed to load folders.'));
    } finally {
      setLoading(false);
    }
  };

  const handleNavigateFolder = async (folder: SPOFolderInfo) => {
    if (!selectedDrive) return;
    setFolderStack((prev) => [...prev, folder]);
    setLoading(true);
    setError('');
    try {
      const { folders: result } = await api.projects.spo.folders(selectedDrive.id, folder.id);
      setFolders(result);
    } catch (err) {
      setError(errMessage(err, 'Failed to load folder contents.'));
    } finally {
      setLoading(false);
    }
  };

  const handleBack = async () => {
    if (step === 'drives') {
      setStep('sites');
      setSelectedSite(null);
      setDrives([]);
    } else if (step === 'folders') {
      if (folderStack.length > 0) {
        // Navigate up one folder level
        const newStack = [...folderStack];
        newStack.pop();
        setFolderStack(newStack);
        setLoading(true);
        setError('');
        try {
          const parentId = newStack.length > 0 ? newStack[newStack.length - 1].id : undefined;
          const { folders: result } = await api.projects.spo.folders(selectedDrive!.id, parentId);
          setFolders(result);
        } catch (err) {
          setError(errMessage(err, 'Failed to navigate back.'));
        } finally {
          setLoading(false);
        }
      } else {
        // Back to drives
        setStep('drives');
        setSelectedDrive(null);
        setFolders([]);
      }
    }
  };

  /** Confirm selection at drive root or current folder. */
  const handleConfirmSelection = () => {
    if (!selectedSite || !selectedDrive) return;
    const currentFolder = folderStack.length > 0 ? folderStack[folderStack.length - 1] : undefined;
    onSelect({
      siteId: selectedSite.id,
      siteUrl: selectedSite.webUrl,
      siteName: selectedSite.displayName,
      driveId: selectedDrive.id,
      driveName: selectedDrive.name,
      folderId: currentFolder?.id,
      folderName: currentFolder?.name,
    });
  };

  // Breadcrumb trail
  const breadcrumbs: string[] = [];
  if (selectedSite) breadcrumbs.push(selectedSite.displayName);
  if (selectedDrive) breadcrumbs.push(selectedDrive.name);
  folderStack.forEach((f) => breadcrumbs.push(f.name));

  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden">
      {/* Header with breadcrumbs */}
      <div className="px-4 py-3 bg-slate-50 border-b border-slate-200 flex items-center gap-2">
        {step !== 'sites' && (
          <button
            onClick={handleBack}
            className="p-1 text-slate-400 hover:text-slate-600 transition-colors"
            title="Go back"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
        )}
        <div className="flex items-center gap-1 text-xs text-slate-500 overflow-hidden">
          <span className="font-medium text-slate-600">SharePoint</span>
          {breadcrumbs.map((crumb, i) => (
            <span key={i} className="flex items-center gap-1">
              <ChevronRight className="w-3 h-3 shrink-0" />
              <span className="truncate">{crumb}</span>
            </span>
          ))}
        </div>
      </div>

      {/* Paste-a-site-URL bar (sites step only) — the escape hatch for a site
          other than the org's configured one, and a hard bypass of site search. */}
      {step === 'sites' && (
        <div className="px-4 py-2 border-b border-slate-100">
          <div className="relative flex items-center gap-2">
            <div className="relative flex-1">
              <Link2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-300" />
              <input
                type="text"
                value={urlInput}
                onChange={(e) => setUrlInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleResolveUrl();
                  }
                }}
                placeholder="Paste a SharePoint site or folder URL…"
                className="w-full pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-orange/30 focus:border-brand-orange"
              />
            </div>
            <button
              onClick={handleResolveUrl}
              disabled={resolving || !urlInput.trim()}
              className="px-3 py-2 text-sm font-medium text-white bg-brand-orange rounded-lg hover:bg-orange-600 transition-colors disabled:opacity-40 disabled:cursor-not-allowed shrink-0"
            >
              {resolving ? <LoaderCircle className="w-4 h-4 animate-spin" /> : 'Add'}
            </button>
          </div>
        </div>
      )}

      {/* Error banner */}
      {error && (
        <div className="px-4 py-2 bg-red-50 border-b border-red-100 text-xs text-red-600">
          {error}
        </div>
      )}

      {/* Content area */}
      <div className="max-h-72 overflow-y-auto">
        {loading && (
          <div className="flex items-center justify-center py-12 text-sm text-slate-400 gap-2">
            <LoaderCircle className="w-4 h-4 animate-spin" />
            Loading...
          </div>
        )}

        {/* Sites list */}
        {!loading && step === 'sites' && sites.length === 0 && (
          <div className="py-8 text-center text-sm text-slate-400">
            No SharePoint site available. Paste a site URL above to browse it.
          </div>
        )}
        {!loading &&
          step === 'sites' &&
          sites.map((site) => (
            <button
              key={site.id}
              onClick={() => handleSelectSite(site)}
              className="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-50 transition-colors text-left border-b border-slate-100 last:border-b-0"
            >
              <Globe className="w-5 h-5 text-blue-500 shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-brand-charcoal truncate">
                  {site.displayName}
                </p>
                <p className="text-xs text-slate-400 truncate">{site.webUrl}</p>
              </div>
              <ChevronRight className="w-4 h-4 text-slate-300 shrink-0" />
            </button>
          ))}

        {/* Drives list */}
        {!loading && step === 'drives' && drives.length === 0 && (
          <div className="py-8 text-center text-sm text-slate-400">
            No document libraries found in this site.
          </div>
        )}
        {!loading &&
          step === 'drives' &&
          drives.map((drive) => (
            <button
              key={drive.id}
              onClick={() => handleSelectDrive(drive)}
              className="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-50 transition-colors text-left border-b border-slate-100 last:border-b-0"
            >
              <HardDrive className="w-5 h-5 text-amber-500 shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-brand-charcoal truncate">
                  {drive.name}
                </p>
                {drive.description && (
                  <p className="text-xs text-slate-400 truncate">{drive.description}</p>
                )}
              </div>
              <ChevronRight className="w-4 h-4 text-slate-300 shrink-0" />
            </button>
          ))}

        {/* Folders list */}
        {!loading && step === 'folders' && folders.length === 0 && (
          <div className="py-8 text-center text-sm text-slate-400">
            No subfolders in this location.
          </div>
        )}
        {!loading &&
          step === 'folders' &&
          folders.map((item) => (
            <button
              key={item.id}
              onClick={() => handleNavigateFolder(item)}
              className="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-50 transition-colors text-left border-b border-slate-100 last:border-b-0"
            >
              <Folder className="w-5 h-5 text-brand-charcoal shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-brand-charcoal truncate">
                  {item.name}
                </p>
                {typeof item.childCount === 'number' && (
                  <p className="text-xs text-slate-400">{item.childCount} items</p>
                )}
              </div>
              <ChevronRight className="w-4 h-4 text-slate-300 shrink-0" />
            </button>
          ))}
      </div>

      {/* Selection footer (folders step) */}
      {step === 'folders' && selectedDrive && (
        <div className="px-4 py-3 bg-slate-50 border-t border-slate-200 flex items-center justify-between">
          <div className="text-xs text-slate-500 truncate mr-4">
            {folderStack.length > 0
              ? `Select "${folderStack[folderStack.length - 1].name}" or browse deeper`
              : `Select "${selectedDrive.name}" root or browse into a folder`}
          </div>
          <button
            onClick={handleConfirmSelection}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-brand-orange rounded-lg hover:bg-orange-600 transition-colors shrink-0"
          >
            <Check className="w-4 h-4" />
            Select This Location
          </button>
        </div>
      )}
    </div>
  );
}
